import React, { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { smlNavigationMessages } from '../../navigation/smlNavigationMessages';
import { PromoteContainer } from '../PromoteContainer';
import { useReelDraft } from './aiReel/useReelDraft';
import { useReelActions } from './aiReel/useReelActions';
import { ReelProgressPage } from './aiReel/ReelProgressPage';
import { ReelFeatureOffAlert } from './aiReel/ReelFeatureOffAlert';
import { useStudioSong } from './useStudioSong';
import { useStudioDraft } from './useStudioDraft';
import { StudioSongPicker } from './StudioSongPicker';
import { StudioNoSongs } from './picker/StudioNoSongs';
import { StudioSongView } from './StudioSongView';
import { studioMessages } from './studioMessages';
import { StudioWorkChip } from '../work/StudioWorkChip';
import { StudioWorkPanel } from '../work/StudioWorkPanel';
import { useStudioWorkPanel } from '../work/useStudioWorkPanel';

interface StudioScreenProps {
  accountId: number;
}

/**
 * The Studio (spec D26, D28; designs t1, t2, n1–n7). It replaces Reel Studio: a song, then every
 * asset type on one screen, then the details of one. With no song it shows the song picker. The
 * AI reel is the Short theme `aiReel`: its step 3 is the reel form, and `?reel=<uuid>` opens the reel's own page.
 * Navigation v2 (design c 3e): the Work chip in the head opens the Work panel (`?work=1`).
 */
const StudioScreenComponent: React.FC<StudioScreenProps> = ({ accountId }) => {
  const intl = useIntl();
  const { catalog, item, song } = useStudioSong(accountId);
  const draft = useStudioDraft(song);
  const workPanel = useStudioWorkPanel();
  const items = catalog.data?.items;

  const reelDraft = useReelDraft({ accountId, initialCatalogItemUuid: draft.catalogItemUuid, items });
  const reelActions = useReelActions({ draft: reelDraft, reelUuid: draft.reelUuid });
  // The song of the reel form is the Studio's song.
  const { setCatalogItemUuid } = reelDraft;
  useEffect(() => {
    if (draft.catalogItemUuid !== undefined) {
      setCatalogItemUuid(draft.catalogItemUuid);
    }
  }, [draft.catalogItemUuid, setCatalogItemUuid]);

  const { refetch } = catalog;
  const refetchCatalog = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);
  // A 400 M24_NO_MASTER / M24_MASTER_PROCESSING on a reel means the row changed: read it again.
  useEffect(() => {
    if (reelActions.songRefusal !== null) {
      refetchCatalog();
    }
  }, [refetchCatalog, reelActions.songRefusal]);

  const studioTitle = intl.formatMessage(smlNavigationMessages.promoteStudio);
  const crumbs = useMemo(() => (item === undefined ? [studioTitle] : [studioTitle, item.title]), [item, studioTitle]);
  const isAiReel = draft.themeId === 'aiReel';

  if (isAiReel && (!song.canUseAiReels || reelActions.refusal === 'M24_LIMIT_FEATURE')) {
    return (
      <PromoteContainer title={studioTitle}>
        <ReelFeatureOffAlert />
      </PromoteContainer>
    );
  }

  if (isAiReel && draft.reelUuid !== '') {
    return (
      <ReelProgressPage
        accountId={accountId}
        reelUuid={draft.reelUuid}
        items={items}
        isRetrying={reelActions.isGenerating}
        tryAgainError={reelActions.tryAgainError}
        onTryAgain={reelActions.tryAgain}
        onChangeSong={reelActions.changeSong}
        onChangeSettings={reelActions.openForm}
      />
    );
  }

  if (catalog.isLoading) {
    return <LoadingView />;
  }

  const title = item === undefined ? studioTitle : intl.formatMessage(studioMessages.titleSong, { title: item.title });

  return (
    <PromoteContainer
      title={title}
      crumbs={crumbs}
      actions={<StudioWorkChip onOpen={workPanel.open} isOpen={workPanel.isOpen} />}
    >
      <StudioWorkPanel accountId={accountId} isOpen={workPanel.isOpen} onClose={workPanel.close} />
      {catalog.error !== undefined && <ErrorView error={catalog.error} retry={refetchCatalog} />}
      {items?.length === 0 && <StudioNoSongs accountId={accountId} />}
      {items !== undefined && items.length > 0 && item === undefined && (
        <StudioSongPicker accountId={accountId} items={items} onPick={draft.chooseSong} />
      )}
      {item !== undefined && (
        <StudioSongView
          accountId={accountId}
          item={item}
          song={song}
          draft={draft}
          reelDraft={reelDraft}
          reelActions={reelActions}
          onNoMaster={refetchCatalog}
        />
      )}
    </PromoteContainer>
  );
};

export const StudioScreen = TypedMemo(StudioScreenComponent);
